import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Button, Typography } from '@material-ui/core'
import HighlightOffIcon from '@material-ui/icons/HighlightOff'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../Providers/AuthProvider'
import { ConversationContext } from '../../Providers/ConversationProvider'
import { TopicContext } from '../../Providers/TopicProvider'
import { GoogleCredentialResponse, googleClientId, loadGoogleIdentity } from './googleIdentity'

interface Props {
  label?: string
  style?: React.CSSProperties
  /**
   * What to do once the sign-in has actually succeeded -- close the dialog,
   * move to signup step 2, whatever the host form does for its Facebook and
   * email paths. Optional: a call site that omits it still signs the user in,
   * it just leaves them where they were.
   */
  loginComplete?(): void
}

type Status = 'loading' | 'ready' | 'unconfigured' | 'unavailable' | 'failed'

const BUTTON_WIDTH = 265

/**
 * Sign in / sign up with Google.
 *
 * Rendered in three places -- LoginForm, LoginFormSso and Signup1Form -- so the
 * signup path goes through exactly this component too.
 *
 * Why Google's own rendered button rather than the One Tap prompt:
 *
 *   * `google.accounts.id.prompt()` is suppressed whenever GSI has set its
 *     `g_state` cookie (the user dismissed One Tap recently), when the browser
 *     blocks third-party state, or when FedCM declines -- and it is suppressed
 *     SILENTLY. That was the old failure mode: click the button, nothing at all
 *     happens, no error anywhere but the console. The app worked around it by
 *     calling `fetch('/removeCookie')` -- an endpoint on this repo's own
 *     Express server (`server/index.js`) that clears `g_state` -- before every
 *     prompt.
 *   * `renderButton` is not subject to that cooldown. A click always opens the
 *     account chooser, which is what makes "it must be impossible to click and
 *     observe nothing" achievable at all.
 *
 * Every other outcome (no client ID, script blocked, Google returned no
 * credential, the API rejected it) sets a status that renders a visible
 * message. `console.error` alone was the old behaviour and is not enough.
 */
export default function GoogleLogin({ label, style, loginComplete }: Props) {
  const { loginWithGoogle } = useAuth()
  const { topic } = useContext(TopicContext)
  const { conversation } = useContext(ConversationContext)
  const { t, i18n } = useTranslation()

  const clientId = googleClientId()
  const containerRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<Status>(clientId ? 'loading' : 'unconfigured')

  // The signup metadata the API still reads. Kept in a ref so the GSI callback
  // -- registered once, at mount -- always sees the current value without
  // having to re-run `initialize`.
  const signupContext = useRef({ signedUpTopicId: topic?.slug, signedUpConversationId: conversation?.id })
  signupContext.current = { signedUpTopicId: topic?.slug, signedUpConversationId: conversation?.id }

  // Same reason, and it is not optional here: every call site redefines
  // `loginComplete` on each render, so listing it in `handleCredential`'s
  // dependencies would change that callback's identity every render, re-run
  // the effect below, and rebuild Google's iframe each time. The ref keeps the
  // effect's dependencies stable while the callback stays current.
  const onComplete = useRef(loginComplete)
  onComplete.current = loginComplete

  const handleCredential = useCallback(
    (response: GoogleCredentialResponse) => {
      // Google can invoke the callback with no credential (the user closed the
      // chooser, or FedCM aborted). Silence here is the bug we are fixing.
      if (!response?.credential) {
        setStatus('failed')
        return
      }

      setStatus('ready')

      loginWithGoogle({
        credential: response.credential,
        locale: i18n.language,
        ...signupContext.current
      }).then(
        () => {
          // Success only. The host form does the navigating -- close the login
          // dialog, move to signup step 2 -- exactly as it already does for its
          // Facebook and email paths. Running this on a failure would carry an
          // unauthenticated user off the form and hide the message below.
          onComplete.current && onComplete.current()
        },
        () => {
          // A rejected verification, a 401, a network failure -- all of them have
          // to reach the user. `loginWithGoogle` used to resolve silently when
          // the response carried no auth_token.
          //
          // Deliberately `then(onSuccess, onFailure)` rather than a trailing
          // `.catch`: a `.catch` after the success handler would also swallow
          // anything the host form's navigation throws and report it here as
          // "Google sign-in failed", which is a different lie from the one
          // PR#741 removed.
          setStatus('failed')
        }
      )
    },
    [loginWithGoogle, i18n.language]
  )

  useEffect(() => {
    if (!clientId) return

    let cancelled = false

    loadGoogleIdentity()
      .then((google) => {
        if (cancelled || !containerRef.current) return

        google.initialize({
          client_id: clientId,
          callback: handleCredential,
          // Never sign somebody in without a click. auto_select would re-use a
          // remembered account on page load.
          auto_select: false,
          cancel_on_tap_outside: true
        })

        google.renderButton(containerRef.current, {
          type: 'standard',
          theme: 'filled_blue',
          size: 'large',
          shape: 'rectangular',
          text: label ? 'signup_with' : 'signin_with',
          logo_alignment: 'left',
          width: BUTTON_WIDTH,
          locale: i18n.language
        })

        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('unavailable')
      })

    return () => {
      cancelled = true
    }
  }, [clientId, handleCredential, label, i18n.language])

  const message = () => {
    if (status === 'unconfigured') return t('auth.googleNotConfigured')
    if (status === 'unavailable') return t('auth.googleUnavailable')
    if (status === 'failed') return t('auth.googleFailed')
    return null
  }

  const text = message()

  return (
    <div>
      {/* The real button is rendered into this node by GSI. It stays empty when
          Google is unreachable, which is why the fallback below is not
          conditional on `status === 'ready'` alone. */}
      <div ref={containerRef} data-testid='google-signin-button' style={{ display: 'flex', justifyContent: 'center', ...style }} />

      {(status === 'unconfigured' || status === 'unavailable') && (
        <Button
          disabled
          color='primary'
          size='large'
          variant='contained'
          data-testid='google-signin-disabled'
          style={{ marginTop: '1em', width: `${BUTTON_WIDTH}px`, height: '48px', fontStyle: 'normal', fontWeight: 800, fontSize: '18px', lineHeight: '26px', ...style }}
        >
          {label || t('auth.googleLogin')}
        </Button>
      )}

      {text && (
        <div style={{ backgroundColor: '#FAEBEB', padding: '.5rem', borderRadius: '.5rem', marginTop: '.5rem' }}>
          <Typography variant='body1' color='error' align='center' component='div' style={{ display: 'flex', justifyContent: 'center' }} role='alert'>
            <HighlightOffIcon style={{ fontSize: '1rem', marginRight: '.25rem', marginTop: '.25rem' }} />
            {text}
          </Typography>
        </div>
      )}
    </div>
  )
}
